/**
 * Contour simplifié de la France métropolitaine et de la Corse, et la
 * projection qui va avec.
 *
 * Les points sont de vraies coordonnées (longitude, latitude) de villes et de
 * caps, pas un dessin approximatif : c'est ce qui permettra de placer plus tard
 * un utilisateur géolocalisé avec `project()`, dans le même repère que le
 * contour. Un tracé « à l'oeil » aurait obligé à tout refaire à ce moment-là.
 */

/** Bornes de la métropole, Corse comprise (Ouessant → Corse est, Corse sud → Dunkerque). */
const LON_MIN = -5.0;
const LAT_MAX = 51.1;

/**
 * Facteur de rétrécissement des longitudes à la latitude moyenne du pays
 * (cos 46,5°). Sans lui la France serait étirée d'un tiers en largeur : un
 * degré de longitude y vaut environ 69 % d'un degré de latitude.
 */
const LON_SCALE = 0.688;

/** Échelle : 100 unités de large dans le `viewBox`. */
const K = 9.955;

export const MAP_WIDTH = 100;
export const MAP_HEIGHT = 98;

/** Coordonnées géographiques → repère du `viewBox`. */
export const project = (lon: number, lat: number): [number, number] => [
  (lon - LON_MIN) * LON_SCALE * K,
  (LAT_MAX - lat) * K,
];

// Sens horaire depuis Dunkerque : côte de la Manche, Bretagne, littoral
// atlantique, Pyrénées, littoral méditerranéen, Alpes, Rhin, frontière nord.
const MAINLAND: [number, number][] = [
  [2.38, 51.03], [1.85, 50.95], [1.08, 49.93], [0.10, 49.49],
  [-1.62, 49.63], [-1.60, 48.84], [-2.02, 48.65], [-4.49, 48.39],
  [-4.74, 48.04], [-3.37, 47.75], [-2.20, 47.28], [-1.78, 46.50],
  [-1.15, 46.16], [-1.03, 45.62], [-1.17, 44.66], [-1.56, 43.48],
  [-0.37, 42.80], [1.53, 42.50], [3.03, 42.55], [3.00, 43.18],
  [3.88, 43.61], [5.37, 43.30], [5.93, 43.12], [7.26, 43.70],
  [6.63, 44.90], [6.87, 45.83], [6.14, 46.20], [7.59, 47.56],
  [7.75, 48.58], [8.23, 48.97], [6.17, 49.36], [4.72, 49.77],
  [3.97, 50.28], [3.06, 50.63],
];

const CORSICA: [number, number][] = [
  [9.35, 43.00], [9.55, 42.60], [9.55, 41.85], [9.28, 41.37],
  [8.78, 41.55], [8.55, 42.25], [8.70, 42.60], [9.00, 42.90],
];

const toPath = (points: [number, number][]): string =>
  `${points
    .map(([lon, lat], index) => {
      const [x, y] = project(lon, lat);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ')} Z`;

export const FRANCE_MAINLAND_PATH = toPath(MAINLAND);
export const CORSICA_PATH = toPath(CORSICA);
